import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "map",
    "editButton",
    "findButton",
    "editBlock",
    "findBlock",
    "mapBlock",
  ];
  static values = {
    lat: Number,
    long: Number,
    organizationName: String,
  };

  connect() {
    console.log("Google Places controller connected");
    this.initializeGooglePlaces();
  }

  initializeGooglePlaces() {
    // Check if Google Places API is loaded
    if (typeof google === "undefined" || !google.maps || !google.maps.places) {
      console.warn("Google Maps/Places API not loaded");
      return;
    }

    // Initialize map if it doesn't exist
    if (!this.mapValue) {
      this.mapValue = this.initializeMap();
    }

    // Set up button states based on existing Google Place ID
    this.updateButtonStates();
  }

  initializeMap() {
    if (!this.mapTarget) return null;

    const mapElement = this.mapTarget;
    const lat = this.latValue || 0;
    const lng = this.longValue || 0;

    const map = new google.maps.Map(mapElement, {
      center: { lat, lng },
      zoom: 10,
    });

    // Store map reference
    window.google_place_map = map;
    return map;
  }

  updateButtonStates() {
    const googlePlaceId = this.getGooglePlaceId();

    if (googlePlaceId) {
      this.showEditBlock();
    } else {
      this.showFindBlock();
    }
  }

  showEditBlock() {
    if (this.hasEditBlockTarget)
      this.editBlockTarget.classList.remove("hidden");
    if (this.hasFindBlockTarget) this.findBlockTarget.classList.add("hidden");
  }

  showFindBlock() {
    if (this.hasEditBlockTarget) this.editBlockTarget.classList.add("hidden");
    if (this.hasFindBlockTarget)
      this.findBlockTarget.classList.remove("hidden");
  }

  showMap() {
    if (this.hasMapBlockTarget) this.mapBlockTarget.classList.remove("hidden");
    if (this.hasEditBlockTarget) this.editBlockTarget.classList.add("hidden");
    if (this.hasFindBlockTarget) this.findBlockTarget.classList.add("hidden");
  }

  hideMap() {
    if (this.hasMapBlockTarget) this.mapBlockTarget.classList.add("hidden");
    this.updateButtonStates();
  }

  findGooglePlace() {
    const address = this.buildAddressString();

    if (!this.validateAddress(address)) {
      this.showError("You must fill in all address fields");
      return;
    }

    this.showMap();
    this.centerMapOnAddress(address);
    this.searchNearbyPlaces();
  }

  buildAddressString() {
    const country = this.getSelectText("contact_country_id");
    const address = this.getInputValue("contact_address_lines");
    const city = this.getInputValue("contact_city");
    const subdivision = this.getSelectText("contact_subdivision_id");
    const postalZip = this.getInputValue("contact_postal_zip");

    return `${address}, ${city}, ${subdivision}, ${postalZip}, ${country}`;
  }

  validateAddress(address) {
    const requiredFields = [
      "contact_country_id",
      "contact_address_lines",
      "contact_city",
      "contact_subdivision_id",
      "contact_postal_zip",
    ];

    return requiredFields.every((field) => {
      const value = this.getInputValue(field) || this.getSelectText(field);
      return value && value.trim() !== "";
    });
  }

  centerMapOnAddress(addressString) {
    if (!this.mapValue) return;

    const geocoder = new google.maps.Geocoder();
    const options = { address: addressString };

    geocoder.geocode(options, (results, status) => {
      if (status === google.maps.GeocoderStatus.OK && results[0]) {
        const location = results[0].geometry.location;
        this.mapValue.setCenter(location);
        this.mapValue.setZoom(10);
        google.maps.event.trigger(this.mapValue, "resize");
      }
    });
  }

  searchNearbyPlaces() {
    if (!this.mapValue) return;

    const service = new google.maps.places.PlacesService(this.mapValue);
    const orgName =
      this.getInputValue("common_name") || this.organizationNameValue;
    const center = this.mapValue.getCenter();

    if (!orgName || !center) return;

    service.textSearch(
      {
        query: orgName,
        location: center,
        radius: 50000, // 50km in meters
        type: ["wine"],
      },
      (results, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK) {
          results.forEach((place) => this.createPlaceMarker(place));
        } else {
          console.log("Could not return results from Google Places Service");
        }
      }
    );
  }

  createPlaceMarker(place) {
    if (!this.mapValue) return;

    const marker = new google.maps.Marker({
      map: this.mapValue,
      position: place.geometry.location,
      title: place.name,
    });

    // Add info window
    const infoWindow = new google.maps.InfoWindow({
      content: `
        <div>
          <h3>${place.name}</h3>
          <p>${place.formatted_address || ""}</p>
          <button onclick="window.googlePlacesController.selectPlace('${
            place.place_id
          }', '${place.name}')" 
                  class="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700">
            Select This Place
          </button>
        </div>
      `,
    });

    marker.addListener("click", () => {
      infoWindow.open(this.mapValue, marker);
    });
  }

  selectPlace(placeId, placeName) {
    this.setGooglePlaceId(placeId);
    this.showEditBlock();
    this.hideMap();

    // Show success message
    this.showSuccess(`Selected Google Place: ${placeName}`);
  }

  editGooglePlace() {
    // This could open a modal or redirect to edit the place
    console.log("Edit Google Place functionality");
  }

  removeGooglePlace() {
    this.setGooglePlaceId("");
    this.updateButtonStates();
    this.showSuccess("Google Place removed");
  }

  // Utility methods
  getInputValue(fieldName) {
    const input = document.querySelector(`input[name*="${fieldName}"]`);
    return input ? input.value : "";
  }

  getSelectText(fieldName) {
    const select = document.querySelector(`select[name*="${fieldName}"]`);
    return select ? select.options[select.selectedIndex]?.text : "";
  }

  getGooglePlaceId() {
    const input = document.querySelector('input[name*="google_place_id"]');
    return input ? input.value : "";
  }

  setGooglePlaceId(placeId) {
    const input = document.querySelector('input[name*="google_place_id"]');
    if (input) input.value = placeId;
  }

  showError(message) {
    // Simple error display - you could enhance this with a toast notification
    alert(message);
  }

  showSuccess(message) {
    // Simple success display - you could enhance this with a toast notification
    console.log(message);
  }
}
